// ============================================
// MENUMOBILE.JS — Menú overlay mobile con pila de pantallas
// ============================================
// - Click en categoría con hijos: REEMPLAZA la pantalla por sus hijos
// - Header adaptativo: muestra nombre de la categoría + botón "Ver todas"
// - Botón "Volver" arriba en sub-niveles
// - Scroll definitivo: flex 1 1 auto + minHeight 0 + overflowY scroll
// ============================================

import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  API_BASE_CLI, PP, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CLARO, COLOR_BORDE,
  COLOR_PRINCIPAL, estiloInput,
} from './helpersClientes';
import { IconoCategoria } from '../utils/iconosCategorias';
import { tokens } from '../styles/tokens';
import { authHeaders } from '../utils/storage';

export default function MenuMobile({ open, onCerrar, onSeleccionar }) {
  const [arbol, setArbol] = useState({ beep: null, syscom: [] });
  const [cargando, setCargando] = useState(true);
  const [pila, setPila] = useState([]);   // stack de nodos visitados
  const [filtro, setFiltro] = useState('');

  // Body lock simple — solo overflow:hidden, sin position fixed
  useEffect(() => {
    if (!open) return;
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, [open]);

  // ESC cierra
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') cerrarTodo(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line
  }, [open]);

  // Carga el árbol al abrir por primera vez
  useEffect(() => {
    if (!open) return;
    if (arbol.syscom.length > 0 || arbol.beep) return;
    let activo = true;
    setCargando(true);
    Promise.all([
      fetch(`${API_BASE_CLI}/api/tienda/categorias-arbol`, { headers: authHeaders() }).then(x => x.json()).catch(() => null),
      fetch(`${API_BASE_CLI}/api/syscom/categorias-arbol`, { headers: authHeaders() }).then(x => x.json()).catch(() => null),
    ]).then(([beepRes, syscomRes]) => {
      if (!activo) return;
      const beep = beepRes?.ok && Array.isArray(beepRes.categorias)
        ? beepRes.categorias.find(c => c.id === 'beep')
        : null;
      const syscom = syscomRes?.ok && Array.isArray(syscomRes.arbol)
        ? syscomRes.arbol.map(normalizarSyscom)
        : [];
      setArbol({ beep, syscom });
    }).finally(() => { if (activo) setCargando(false); });
    return () => { activo = false; };
    // eslint-disable-next-line
  }, [open]);

  // Calcular nivelActual + lista (todos los hooks ANTES de cualquier return)
  const nivelActual = pila[pila.length - 1] || null;

  const listaBase = useMemo(() => {
    if (!nivelActual) {
      const out = [];
      if (arbol.beep) out.push(arbol.beep);
      out.push(...(arbol.syscom || []));
      return out;
    }
    return Array.isArray(nivelActual.children) ? nivelActual.children : [];
  }, [arbol, nivelActual]);

  const lista = useMemo(() => {
    if (pila.length > 0 || !filtro.trim()) return listaBase;
    const q = filtro.trim().toLowerCase();
    return listaBase.filter(c => (c.nombre || '').toLowerCase().includes(q));
  }, [listaBase, filtro, pila.length]);

  // Resetea pila y filtro al cerrar
  const cerrarTodo = () => {
    setPila([]);
    setFiltro('');
    onCerrar && onCerrar();
  };

  if (!open) return null;

  const entrar = (cat) => setPila(p => [...p, cat]);
  const volver = () => setPila(p => p.slice(0, -1));
  const verTodas = (cat) => {
    onSeleccionar && onSeleccionar(cat ? cat.id : null);
    cerrarTodo();
  };

  const contenido = (
    <div
      data-mega-menu-portal="true"
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        width: '100vw', height: '100vh',
        background: '#fff', zIndex: 99999,
        display: 'flex', flexDirection: 'column',
        fontFamily: PP, color: COLOR_GRIS,
      }}
    >
      {/* Header altura fija */}
      <div style={{
        height: 56, flexShrink: 0,
        padding: '0 12px',
        borderBottom: `1px solid ${COLOR_BORDE}`,
        display: 'flex', alignItems: 'center', gap: 8,
        background: '#fff',
      }}>
        <button
          onClick={cerrarTodo}
          aria-label="Cerrar"
          style={btnHeader}
        >✕</button>
        <h2 style={{
          flex: 1, margin: 0, fontSize: 16, fontWeight: 700, color: COLOR_OSCURO,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {nivelActual ? nivelActual.nombre : 'Categorías'}
        </h2>
        {nivelActual && (
          <button
            onClick={() => verTodas(nivelActual)}
            style={{
              background: COLOR_PRINCIPAL, color: '#fff',
              padding: '8px 14px', borderRadius: 99, border: 'none',
              fontWeight: 600, fontSize: 13, fontFamily: PP, cursor: 'pointer',
              minHeight: 36,
              touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
            }}
          >Ver todas</button>
        )}
      </div>

      {/* Volver (en sub-niveles) */}
      {nivelActual && (
        <button
          onClick={volver}
          style={{
            flexShrink: 0,
            padding: '14px 16px',
            background: tokens.surfaceSubtle, border: 'none',
            borderBottom: `1px solid ${COLOR_BORDE}`,
            display: 'flex', alignItems: 'center', gap: 8,
            color: COLOR_PRINCIPAL, fontWeight: 600, fontSize: 14,
            width: '100%', textAlign: 'left', cursor: 'pointer',
            fontFamily: PP, minHeight: 48,
            touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
          }}
        >← Volver</button>
      )}

      {/* Buscador (solo root) */}
      {!nivelActual && (
        <div style={{ flexShrink: 0, padding: '12px 16px' }}>
          <input
            style={{ ...estiloInput, fontSize: 16, padding: '12px 14px', minHeight: 48 }}
            placeholder="🔍 Buscar categoría…"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
          />
        </div>
      )}

      {/* Lista scrolleable — flex 1 1 0 + minHeight 0 + overflowY auto */}
      <div
        data-mega-menu-lista="true"
        style={{
          flex: '1 1 0',
          minHeight: 0,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 120px)',
        }}
      >
        {cargando && (
          <div style={{ padding: 30, textAlign: 'center', color: COLOR_GRIS_CLARO, fontSize: 13 }}>
            Cargando categorías…
          </div>
        )}
        {!cargando && !nivelActual && (
          <Item
            icono="⭐"
            nombre="Todos los productos"
            destacado
            onClick={() => verTodas(null)}
          />
        )}
        {!cargando && lista.map(c => (
          <Item
            key={c.id}
            imagenUrl={c.imagen_url}
            nombre={c.nombre}
            contador={c.contador}
            tieneHijos={Array.isArray(c.children) && c.children.length > 0}
            onClick={() => {
              if (Array.isArray(c.children) && c.children.length > 0) entrar(c);
              else verTodas(c);
            }}
          />
        ))}
        {!cargando && lista.length === 0 && filtro && (
          <div style={{ padding: 30, textAlign: 'center', color: COLOR_GRIS_CLARO, fontSize: 13 }}>
            Sin categorías que coincidan con "{filtro}"
          </div>
        )}
        {!cargando && lista.length === 0 && !filtro && (
          <div style={{ padding: 30, textAlign: 'center', color: COLOR_GRIS_CLARO, fontSize: 13, lineHeight: 1.5 }}>
            Categorías no sincronizadas. Pide al admin que sincronice
            el árbol desde el <strong>panel de administración</strong>.
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(contenido, document.body);
}

function normalizarSyscom(n) {
  return {
    id: `syscom_${n.id}`,
    raw_id: n.id,
    nombre: n.nombre,
    icono: null,
    imagen_url: n.imagen_url || null,
    contador: n.productos_count || 0,
    children: Array.isArray(n.children) ? n.children.map(normalizarSyscom) : [],
  };
}

function Item({ icono, imagenUrl, nombre, contador, tieneHijos, onClick, destacado }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: '14px 16px',
        fontSize: 16,
        fontWeight: destacado ? 700 : 600,
        cursor: 'pointer',
        color: destacado ? COLOR_PRINCIPAL : COLOR_OSCURO,
        background: '#fff',
        borderBottom: `1px solid ${COLOR_BORDE}`,
        display: 'flex', alignItems: 'center', gap: 12,
        minHeight: 56,
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: '50%',
        background: tokens.primary100, color: COLOR_PRINCIPAL,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 22, flexShrink: 0, overflow: 'hidden',
      }}>
        {imagenUrl ? (
          <img src={imagenUrl} alt="" loading="lazy" decoding="async"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (icono ? icono : <IconoCategoria nombre={nombre} size={22} />)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {nombre}
        </div>
        {contador != null && contador > 0 && (
          <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, fontWeight: 500, marginTop: 2 }}>
            {contador} producto{contador === 1 ? '' : 's'}
          </div>
        )}
      </div>
      {tieneHijos && (
        <span style={{ fontSize: 18, color: COLOR_GRIS_CLARO, fontWeight: 700 }}>›</span>
      )}
    </div>
  );
}

const btnHeader = {
  background: 'transparent', border: 'none',
  fontSize: 22, cursor: 'pointer', color: COLOR_OSCURO,
  padding: '4px 8px', minWidth: 44, minHeight: 44,
  fontWeight: 700,
  touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
};
